import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { createManualTeaching } from "@/lib/admin.functions";
import { listFilterOptions } from "@/lib/teachings.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, PlusCircle } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/adicionar")({
  component: AdminAddVideo,
});

const NONE = "__none__";

function AdminAddVideo() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createFn = useServerFn(createManualTeaching);
  const optsFn = useServerFn(listFilterOptions);
  const { data: opts } = useQuery({ queryKey: ["filter-options"], queryFn: () => optsFn() });

  const [url, setUrl] = useState("");
  const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [parashaId, setParashaId] = useState<string>(NONE);
  const [festivityId, setFestivityId] = useState<string>(NONE);
  const [turno, setTurno] = useState<string>(NONE);
  const [customTitle, setCustomTitle] = useState("");

  const create = useMutation({
    mutationFn: () =>
      createFn({
        data: {
          youtubeUrl: url,
          teachingDate: date,
          parashaId: parashaId === NONE ? null : parashaId,
          festivityId: festivityId === NONE ? null : festivityId,
          turno: turno === NONE ? null : (turno as "manha" | "tarde"),
          customTitle: customTitle.trim() || null,
        },
      }),
    onSuccess: (r) => {
      toast.success(`Vídeo adicionado: ${r.title}`);
      queryClient.invalidateQueries({ queryKey: ["admin-teachings"] });
      queryClient.invalidateQueries({ queryKey: ["recent-teachings"] });
      navigate({ to: "/admin/ensinos" });
    },
    onError: (e: any) => toast.error(e?.message ?? "Falha ao adicionar vídeo"),
  });

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/admin/ensinos"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar aos ensinos
        </Link>
        <h1 className="mt-2 flex items-center gap-2 font-serif text-2xl font-semibold text-primary">
          <PlusCircle className="h-6 w-6" /> Adicionar vídeo manualmente
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Use para inserir vídeos que ficaram de fora da sincronização automática ou vídeos
          publicados em outra plataforma.
        </p>
      </div>

      <form
        className="space-y-5 rounded-xl border border-border bg-card p-6"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="url">
            Link do YouTube <span className="text-destructive">*</span>
          </Label>
          <Input
            id="url"
            required
            placeholder="https://www.youtube.com/watch?v=... ou https://youtu.be/..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Aceita URLs de vídeo, shorts, live ou o ID do vídeo (11 caracteres).
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="date">
              Data do ensino <span className="text-destructive">*</span>
            </Label>
            <Input
              id="date"
              type="date"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Turno do estudo</Label>
            <Select value={turno} onValueChange={setTurno}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Não especificar</SelectItem>
                <SelectItem value="manha">Manhã (11h)</SelectItem>
                <SelectItem value="tarde">Tarde (15h30)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Porção da Torá (Parashá)</Label>
            <Select value={parashaId} onValueChange={setParashaId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a Parashá" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={NONE}>Nenhuma</SelectItem>
                {opts?.parashiot.map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.number}. {p.name_translit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Festividade</Label>
            <Select value={festivityId} onValueChange={setFestivityId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a festividade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Nenhuma</SelectItem>
                {opts?.festivities.map((f: any) => (
                  <SelectItem key={f.slug} value={f.slug}>
                    {f.name_pt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="-mt-2 text-xs text-muted-foreground">
          Informe pelo menos uma Parashá ou festividade — usado para os filtros.
        </p>

        <div className="space-y-1.5">
          <Label htmlFor="ct">Título personalizado (opcional)</Label>
          <Input
            id="ct"
            maxLength={200}
            placeholder="Deixe em branco para gerar automaticamente (DD/MM/AAAA | Nome)"
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={() => navigate({ to: "/admin/ensinos" })}>
            Cancelar
          </Button>
          <Button type="submit" disabled={create.isPending || !url || !date}>
            {create.isPending ? "Adicionando..." : "Adicionar vídeo"}
          </Button>
        </div>
      </form>
    </div>
  );
}
